import { isEmpty as l } from "./multiselect-helper.js";
const u = {
  props: {
    /**
     * name attribute to match optional label element
     */
    name: {
      type: String,
      default: ""
    },
    /**
     * Show selected values in the input box in multiple and tag mode.
     * When set to `false`, users cannot remove the last selected option using the delete key.
     */
    showSelectedValues: {
      type: Boolean,
      default: !0
    },
    /**
     * Limit the display of selected options. The rest will be hidden within the limitText string.
     */
    limit: {
      type: Number,
      default: 99999
    },
    /**
     * Function that process the message shown when selected
     * elements pass the defined limit.
     */
    limitText: {
      type: Function,
      default: (e) => `and ${e} more`
    },
    /**
     * Set true to trigger the loading spinner.
     */
    loading: {
      type: Boolean,
      default: !1
    },
    /**
     * Disables the multiselect if true.
     */
    disabled: {
      type: Boolean,
      default: !1
    },
    /**
     * Disables the multiselect when `loading` prop is `true`.
     */
    disableWhileLoading: {
      type: Boolean,
      default: !0
    },
    /**
     * Show no results
     */
    showNoResults: {
      type: Boolean,
      default: !0
    },
    /**
     * text to show when no results are found. alternative to using the `noResult` slot
     */
    noResultsText: {
      type: String,
      default: "No options found. Consider changing the search query."
    },
    /**
     * Show max number of selected elements
     */
    showMaxElements: {
      type: Boolean,
      default: !0
    },
    /**
     * text to show when all avaialble options have been selected
     */
    allSelectedText: {
      type: String,
      default: "All available options selected."
    },
    /**
     * Function to interpolate the custom label
     */
    formatOptionLabel: {
      type: Function,
      default(e, t) {
        return l(e) ? "" : typeof e == "object" ? e[t] : e;
      }
    },
    /**
     * Decide whether to filter the results based on search query.
     * Useful for async filtering, where we search through more complex data.
     */
    internalSearch: {
      type: Boolean,
      default: !0
    },
    /**
     * Array of available options: Objects, Strings or Integers.
     * If array of objects, visible label will default to option.label.
     * If `labelKey` prop is passed, label will equal `option[labelKey]`
     */
    options: {
      type: Array,
      default: () => []
    },
    /**
     * if using custom option slot where content is larger than the default height,
     * you must supply this for rendering optimizations to work.
     */
    optionHeight: {
      type: Number,
      default: 26
    },
    /**
     * Equivalent to the `multiple` attribute on a `<select>` input.
     */
    multiple: {
      type: Boolean,
      default: !1
    },
    /**
     * bound value
     */
    value: {
      type: [Object, Array, String, Number],
      default: () => []
    },
    /**
     * Key to compare objects
     */
    valueKey: {
      type: String,
      default: "id"
    },
    /**
     * Label to look for in option Object
     */
    labelKey: {
      type: String,
      default: "label"
    },
    /**
     * Enable/disable search in options
     * Defaults to true if options count exceeds `searchThreshold`
     */
    searchable: {
      type: Boolean,
      default: null
    },
    /**
     * Clear the search input after selection
     */
    clearOnSelect: {
      type: Boolean,
      default: !0
    },
    /**
     * Hide already selected options
     */
    hideSelectedOptions: {
      type: Boolean,
      default: !1
    },
    /**
     * Equivalent to the `placeholder` attribute on a `<select>` input.
     */
    placeholder: {
      type: [String, Number],
      default: "Select option"
    },
    /**
     * Allow to remove all selected values
     */
    allowEmpty: {
      type: Boolean,
      default: !0
    },
    /**
     * Reset this.model, this.search after this.model changes.
     * Useful if want to create a stateless dropdown.
     */
    stateless: {
      type: Boolean,
      default: !1
    },
    /**
     * Enable/disable closing after selecting an option
     */
    closeOnSelect: {
      type: Boolean,
      default: !0
    },
    /**
     * Disable / Enable tagging
     */
    taggable: {
      type: Boolean,
      default: !1
    },
    /**
     * Determines whether the "create new ..." tag option should be displayed based on the current input value
     * @param {String} inputValue - current input value
     * @return {Boolean} true/false
     */
    isValidNewOption: {
      type: Function,
      default: void 0
    },
    /**
     * String to show alongside a potential tag
     */
    tagPlaceholder: {
      type: String,
      default: null
    },
    /**
     * By default new tags will appear above the search results.
     * Changing to 'bottom' will revert this behaviour
     * and will proritize the search results
     */
    tagPosition: {
      type: String,
      default: "top"
    },
    /**
     * Number of allowed selected options. No limit if 0.
     */
    max: {
      type: [Number, Boolean],
      default: !1
    },
    /**
     * Will be passed with all events as second param.
     * Useful for identifying events origin.
     */
    id: {
      type: [String, Number],
      default: null
    },
    /**
     * Array of keyboard keys to block
     * when selecting
     */
    blockKeys: {
      type: Array,
      default: () => []
    },
    /**
     * Prevent from wiping up the search value
     */
    preserveSearch: {
      type: Boolean,
      default: !1
    },
    /**
     * Select 1st options if value is empty
     */
    preselectFirst: {
      type: Boolean,
      default: !1
    },
    /**
     * Enable/disable highlighting of the pointed value.
     */
    showPointer: {
      type: Boolean,
      default: !0
    },
    /**
     * Name of the property containing
     * the group options
     */
    groupOptionsKey: {
      type: String,
      default: "options"
    },
    /**
     * Name of the property containing
     * the group label
     */
    groupLabelKey: {
      type: String,
      default: "label"
    },
    /**
     * Allow to select all group options
     * by selecting the group label
     */
    groupSelect: {
      type: Boolean,
      default: !1
    },
    /**
     * Displays dividers between grouped options
     */
    groupDividers: {
      type: Boolean,
      default: !0
    },
    /**
     * Extends width to fit container
     */
    fullWidth: {
      type: Boolean,
      default: !1
    },
    /**
     * min width of trigger button/input
     */
    minTriggerWidth: {
      type: [String, Number],
      default: 160
    },
    /**
     * max width of trigger button/input
     */
    maxTriggerWidth: {
      type: [String, Number],
      default: 400
    },
    /**
     * defines the min width of the dropdown menu
     */
    minMenuWidth: {
      type: [String, Number],
      default: 160
    },
    /**
     * defines the max width of the dropdown menu
     */
    maxMenuWidth: {
      type: [String, Number],
      default: 400
    },
    /**
     * defines the max height of the dropdown menu. scrolls when content is taller than available max height.
     */
    maxMenuHeight: {
      type: [String, Number],
      default: 302
    },
    /**
     * size of trigger
     * @values medium, small, mini
     */
    triggerSize: {
      type: String,
      default: "medium",
      validator: (e) => ["mini", "small", "medium"].includes(e)
    },
    /**
     * if option count exceeds this limit, it automatically becomes searchable. pass `false` to prevent this behavior
     */
    searchThreshold: {
      type: [Number, Boolean],
      default: 10
    },
    /**
     * append menu popper to body. when `false`, appends to trigger element
     */
    appendToBody: {
      type: Boolean,
      default: !0
    },
    /**
     * popper.js options
     */
    popperOptions: {
      type: Object,
      default: () => ({})
    },
    /**
     * pass multiple labels in the form of
     * `:labels="{ top: 'label text', bottom: 'label text', suffix: 'label text'}"`
     */
    labels: {
      type: Object,
      default: () => ({})
    },
    /**
     * if using a custom slot for the option, pass additional width to set the auto menu width
     */
    extraLabelWidth: {
      type: Number,
      default: 40
    },
    /**
     * applies error styles to the trigger component
     */
    invalid: {
      type: Boolean,
      default: !1
    },
    /**
     * accepts either a string key to sortBy or a custom function that returns a value to sort by
     */
    sortBy: {
      type: [String, Function],
      default: null
    },
    /**
     * custom sort method function
     */
    sortMethod: {
      type: Function,
      default: null
    },
    /**
     * for taggable multiselects.
     * allows you to not pass in options (or pass in empty `[]`)
     * and use the multiselect as a pure tagging input component
     */
    allowEmptyOptions: {
      type: Boolean,
      default: !1
    },
    /**
     * @ignore
     */
    scrollConfig: {
      type: Object,
      default() {
        return {
          enabled: !0
        };
      }
    }
  }
};
export {
  u as default
};
//# sourceMappingURL=pendo-multiselect-props.js.map
